import { AppAccent } from 'src/app/core/types/app-accent.type';
import { AppVisualContextService } from 'src/app/shared/services/app-visual-context.service';
import { HostBinding, inject, Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { take } from 'rxjs';
import { Arquivo } from 'src/app/core/models/arquivo.model';
import { SgrResponse } from 'src/app/core/models/response.model';
import { FileService } from '../../services/file.service';
import { ModalService } from '../../services/modal.service';
import { PopupService } from '../../services/popup.service';
import { ModalComponent } from '../modal/modal.component';

/*
 * Componente de visualização de arquivos — irmão gêmeo read-only do FileUploadSelectorComponent.
 * Usado em páginas de detalhe para exibir arquivos já persistidos.
 * - Imagens com BASE64: abre carousel no modal.
 * - Imagens sem BASE64: busca no servidor e abre individualmente (sem carousel).
 * - Outros tipos: abre/baixa via FileService (com fallback para busca no servidor).
 */

@Component({
  selector: 'app-file-viewer',
  templateUrl: './file-viewer.component.html',
  styleUrls: ['./file-viewer.component.css'],
})
export class FileViewerComponent {
  @Input() accent?: AppAccent;
  private readonly visualContext = inject(AppVisualContextService);
  @HostBinding('class') get visualClass(): string {
    return this.accent === undefined ? '' : this.visualContext.classForInputs(this.accent);
  }
  @Input({ required: true }) title: string;
  @Input({ required: true }) files: Arquivo[];

  @ViewChild('modalCarousel') modalCarousel: TemplateRef<ModalComponent>;

  protected imageSelectedIndex: number | null = null;
  // Prefixos de data URL por tipo MIME — readonly para evitar recriação a cada chamada
  private readonly IMAGE_PREFIXES: Record<string, string> = {
    'image/png': 'data:image/png;base64,',
    'image/jpg': 'data:image/jpg;base64,',
    'image/jpeg': 'data:image/jpeg;base64,',
    'image/webp': 'data:image/webp;base64,',
    'image/gif': 'data:image/gif;base64,',
  };

  constructor(
    private fileService: FileService,
    private modalService: ModalService,
    private popupService: PopupService
  ) {}

  // Retorna apenas os arquivos de imagem com BASE64 disponível (exibidos no carousel)
  get imageFiles(): Arquivo[] {
    return this.files?.filter(f => f.TIPO?.includes('image/') && !!f.BASE64) ?? [];
  }

  // Dispatcher: imagem com BASE64 → carousel; qualquer outro caso → openFile
  onCardClick(file: Arquivo): void {
    if (file.TIPO?.includes('image/') && file.BASE64) {
      this.openCarousel(file);
    } else {
      this.openFile(file);
    }
  }

  // Abre o carousel posicionado na imagem clicada
  openCarousel(file: Arquivo): void {
    this.imageSelectedIndex = this.imageFiles.indexOf(file);
    const modal = this.modalService.openModalExtraLarge(this.modalCarousel);
    modal
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.imageSelectedIndex = null;
      });
  }

  // Abre/baixa o arquivo. Com BASE64 local: direto. Sem BASE64: busca no servidor (padrão file-download-link)
  openFile(file: Arquivo): void {
    if (file.BASE64) {
      this.fileService.fileViewOrDownload(file);
      return;
    }

    const queryParams = {
      sistema: file.SISTEMA === 1 ? 'sgr' : 'intranet',
      arquivo: file.ARQUIVO,
    };

    this.fileService
      .download({ queryParams, spinnerOff: true })
      .pipe(take(1))
      .subscribe({
        next: (result: SgrResponse) => {
          if (!result['error']) {
            const downloaded: Arquivo = result['data'][0];
            this.fileService.fileViewOrDownload(downloaded);
          }
        },
        error: () => this.popupService.showError('Erro ao abrir arquivo! Contate o suporte.'),
      });
  }

  // Monta o src completo da imagem a partir do BASE64
  getImageSrc(file: Arquivo): string {
    return (this.IMAGE_PREFIXES[file.TIPO] ?? 'data:image/*;base64,') + file.BASE64;
  }

  // Retorna o src do ícone para arquivos não-imagem (padrão file-upload-selector)
  getFileIconSrc(file: Arquivo): string {
    const ext = file.ARQUIVO.split('.').pop()?.toLowerCase();
    return `assets/img/${ext}-icon.png`;
  }

  // Seleciona o slide pelo índice (usado nos dots indicadores)
  selectImage(index: number): void {
    this.imageSelectedIndex = index;
  }

  // Navegação do carousel com guard de null
  prevImage(): void {
    if (this.imageSelectedIndex != null && this.imageSelectedIndex > 0) {
      this.selectImage(this.imageSelectedIndex - 1);
    }
  }

  nextImage(): void {
    if (this.imageSelectedIndex != null && this.imageSelectedIndex < this.imageFiles.length - 1) {
      this.selectImage(this.imageSelectedIndex + 1);
    }
  }
}
